import { ProxyConfig } from '../../config/ProxyConfig';
import { checkVersionGE } from '../../util/checkVersion';

/**
 * Generate a comma separated list of status codes and cache durations. We are
 * using a list because the ksdk has not needed multiple headers with the same
 * name and different values until now, this would have required either
 * refactoring several classes or having a unicorn implementation for EdgeCache.
 * So we are relying on EdgeCache to properly support rfc9110 for this.
 * See: https://www.rfc-editor.org/rfc/rfc9110.html#name-field-lines-and-combined-fi
 *
 * @param proxyConfig - configuration to check if the header needs to be added
 * @returns comma separated list of values.
 */
export function generateStatusCodeHeader(proxyConfig: ProxyConfig): string {
  const arr: string[] = [];
  Object.entries(proxyConfig.statusBasedDurations)
    .filter(([k, v]) => !isNaN(Number(k)) && !isNaN(Number(v)))
    .sort(([a], [b]) => Number(a) - Number(b))
    .forEach(([k, v]) => {
      arr.push(`${k}=${v}`);
    });
  return arr.join(',');
}

export function normalizeVersionString(version?: string): string {
  if (typeof version === 'undefined') {
    return '';
  }
  return version.replace(/[a-zA-Z]|-.*/g, '');
}

/**
 * Utility function to populate any additional headers that are needed for
 * supporting EdgeCache.
 *
 * The X-Kollective-S-Max-Age-Status-Code is only supported for EC v2.2+
 *
 * @param proxyConfig - configuration with information for headers
 * @param version - version of the EdgeCache we are communicating with
 * @returns
 */
function getAdditionalHeaders(
  proxyConfig: ProxyConfig,
  version?: string
): Record<string, string> {
  const addl: Record<string, string> = {};
  version = normalizeVersionString(version);
  if (version && checkVersionGE({ version, min: '2.2' })) {
    const statusStr = generateStatusCodeHeader(proxyConfig);
    if (statusStr) {
      addl['X-Kollective-S-Max-Age-Status-Code'] = statusStr;
    }
  }
  return addl;
}

export default getAdditionalHeaders;
